import React, { useRef, useState, useEffect } from 'react';

export interface ScrollableCardRowProps {
  children: React.ReactNode;
  gap?: number;
  scrollAmount?: number;
  className?: string;
}

export const ScrollableCardRow: React.FC<ScrollableCardRowProps> = ({
  children,
  gap = 14,
  scrollAmount = 360,
  className,
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const checkScroll = () => {
    if (!rowRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = rowRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const handleResize = () => checkScroll();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [children]);

  const scroll = (direction: 'left' | 'right') => {
    if (!rowRef.current) return;
    const delta = direction === 'left' ? -scrollAmount : scrollAmount;
    rowRef.current.scrollBy({ left: delta, behavior: 'smooth' });
    setTimeout(checkScroll, 350);
  };

  return (
    <div
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ position: 'relative', width: '100%' }}
    >
      {/* Netflix-Style Directional Indicator Overlay: Left (<) */}
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scroll('left')}
          aria-label="Scroll shelf left"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 8,
            width: 36,
            zIndex: 20,
            background: 'linear-gradient(to right, rgba(9, 13, 22, 0.95) 0%, rgba(9, 13, 22, 0.65) 70%, transparent 100%)',
            backdropFilter: 'blur(8px)',
            border: 'none',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)',
            borderTopLeftRadius: 10,
            borderBottomLeftRadius: 10,
            color: 'var(--theme-accent, #38bdf8)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            opacity: isHovered ? 1 : 0.85,
            transition: 'opacity 0.2s ease, background 0.2s ease',
          }}
        >
          <span
            style={{
              fontSize: 22,
              fontWeight: 800,
              fontFamily: 'monospace',
              lineHeight: 1,
              userSelect: 'none',
              filter: 'drop-shadow(0 0 6px rgba(56, 189, 248, 0.5))',
            }}
          >
            &lt;
          </span>
        </button>
      )}

      {/* Card Row Container */}
      <div
        ref={rowRef}
        onScroll={checkScroll}
        style={{
          display: 'flex',
          gap,
          overflowX: 'auto',
          paddingBottom: 8,
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </div>

      {/* Netflix-Style Directional Indicator Overlay: Right (>) */}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scroll('right')}
          aria-label="Scroll shelf right"
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 8,
            width: 36,
            zIndex: 20,
            background: 'linear-gradient(to left, rgba(9, 13, 22, 0.95) 0%, rgba(9, 13, 22, 0.65) 70%, transparent 100%)',
            backdropFilter: 'blur(8px)',
            border: 'none',
            borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
            borderTopRightRadius: 10,
            borderBottomRightRadius: 10,
            color: 'var(--theme-accent, #38bdf8)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            opacity: isHovered ? 1 : 0.85,
            transition: 'opacity 0.2s ease, background 0.2s ease',
          }}
        >
          <span
            style={{
              fontSize: 22,
              fontWeight: 800,
              fontFamily: 'monospace',
              lineHeight: 1,
              userSelect: 'none',
              filter: 'drop-shadow(0 0 6px rgba(56, 189, 248, 0.5))',
            }}
          >
            &gt;
          </span>
        </button>
      )}
    </div>
  );
};
